import { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';
import { confirmDialog } from '../components/ConfirmDialog';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toISO(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function mondayOf(d) {
  const c = new Date(d);
  const diff = (c.getDay() + 6) % 7; // Mon=0 ... Sun=6
  c.setDate(c.getDate() - diff);
  c.setHours(0, 0, 0, 0);
  return c;
}
function timeLabel(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});

function ShiftModal({ userId, userName, date, shift, onClose, onSaved }) {
  const [start, setStart] = useState(shift?.startTime || '09:00');
  const [end, setEnd] = useState(shift?.endTime || '17:00');
  const [note, setNote] = useState(shift?.note || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (shift) {
        await api.updateShift(shift.id, { startTime: start, endTime: end, note: note || null });
      } else {
        await api.createShift({ userId, date, startTime: start, endTime: end, note: note || null });
      }
      toast.success('Shift saved');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!(await confirmDialog('Remove this shift?', { danger: true, confirmText: 'Remove' }))) return;
    setDeleting(true);
    try {
      await api.deleteShift(shift.id);
      toast.success('Shift removed');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to remove shift');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{shift ? 'Edit Shift' : 'New Shift'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 14 }}>
          {userName} — {new Date(date).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <form onSubmit={save}>
          <div className="form-row">
            <div className="field"><label>Start Time</label>
              <select value={start} onChange={e => setStart(e.target.value)}>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{timeLabel(t)}</option>)}
              </select>
            </div>
            <div className="field"><label>End Time</label>
              <select value={end} onChange={e => setEnd(e.target.value)}>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{timeLabel(t)}</option>)}
              </select>
            </div>
          </div>
          <div className="field"><label>Note (optional)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. covering for Sam" />
          </div>
          <div className="modal-footer">
            {shift && (
              <button type="button" className="btn btn-danger" onClick={del} disabled={deleting} style={{ marginRight: 'auto' }}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Shifts() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalCtx, setModalCtx] = useState(null); // { userId, userName, date, shift }

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = async () => {
    try {
      const [userList, shiftList] = await Promise.all([
        api.getUsers(),
        api.getShifts(toISO(weekStart), toISO(addDays(weekStart, 6))),
      ]);
      setUsers(userList.filter(u => u.isActive));
      setShifts(shiftList);
    } catch {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const shiftFor = (userId, dateISO) => shifts.find(s => s.userId === userId && s.date === dateISO);

  const openCell = (user, date) => {
    const dateISO = toISO(date);
    const existing = shiftFor(user.id, dateISO);
    setModalCtx({
      userId: user.id,
      userName: user.displayName || user.username,
      date: dateISO,
      shift: existing || null,
    });
  };

  const isToday = (d) => toISO(d) === toISO(new Date());

  return (
    <div className="page">
      <div className="page-header flex-between">
        <div>
          <h2>Shift Schedule</h2>
          <p className="page-sub">Set each staff member's hours for the week — click a cell to add or edit a shift</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex-between" style={{ marginBottom: 14 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(w => addDays(w, -7))}>‹ Previous</button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700 }}>
              {weekStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – {addDays(weekStart, 6).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <button className="btn-sm" style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }} onClick={() => setWeekStart(mondayOf(new Date()))}>
              This week
            </button>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(w => addDays(w, 7))}>Next ›</button>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : users.length === 0 ? (
          <div className="empty-state"><div className="icon"><NavIcon name="users" size={32} /></div><div>No active users to schedule.</div></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ minWidth: 130 }}>Staff</th>
                  {weekDays.map((d, i) => (
                    <th key={i} style={{ textAlign: 'center', color: isToday(d) ? 'var(--accent)' : undefined }}>
                      {DAY_LABELS[i]}<br />
                      <span className="text-muted" style={{ fontWeight: 400, fontSize: 11 }}>{d.getDate()}/{d.getMonth() + 1}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td data-label="Staff" style={{ fontWeight: 600 }}>{u.displayName || u.username}</td>
                    {weekDays.map((d, i) => {
                      const dateISO = toISO(d);
                      const s = shiftFor(u.id, dateISO);
                      return (
                        <td key={i} data-label={DAY_LABELS[i]} style={{ textAlign: 'center', cursor: 'pointer' }}
                          onClick={() => openCell(u, d)}>
                          {s ? (
                            <span className="badge badge-active" style={{ whiteSpace: 'nowrap' }}>
                              {timeLabel(s.startTime)} – {timeLabel(s.endTime)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--muted)', fontSize: 16 }}>+</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalCtx && (
        <ShiftModal
          userId={modalCtx.userId}
          userName={modalCtx.userName}
          date={modalCtx.date}
          shift={modalCtx.shift}
          onClose={() => setModalCtx(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
